import { RouterModule } from '@angular/router';
import { take, map } from 'rxjs/operators';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { AbstractSecurityStorage, AuthModule, LogLevel, OpenIdConfiguration, StsConfigHttpLoader, StsConfigLoader } from 'angular-auth-oidc-client';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { NgModule } from '@angular/core';
import * as RoutesDefinitions from 'apps/console/src/app/routes-definitions';
import { LoaderSpinnerComponent, stripSlashes } from '@aston/foundation';

import { AppConfiguration } from '../app.configuration';

import { AuthenticationService, AuthenticationStorageService } from './services';
import { AuthInterceptor } from './interceptors';
import { AuthenticationGuard, AnonymousGuard } from './guards';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { LoginCallbackPageComponent } from './pages/login-callback-page/login-callback-page.component';
import { LogoutPageComponent } from './pages/logout-page/logout-page.component';
import { AuthenticationLayoutComponent } from './layouts/authentication-layout/authentication-layout.component';
import { RegisterPageComponent } from './pages/register-page/register-page.component';



@NgModule({
	imports: [
		NgOptimizedImage,
		AuthModule.forRoot({
			loader: {
				provide: StsConfigLoader,
				useFactory: configureAuth,
				deps: [AppConfiguration, AuthenticationStorageService],
			},
		}),
		CommonModule,
		LoaderSpinnerComponent,
		RouterModule,
		AuthenticationLayoutComponent,
		LoginCallbackPageComponent,
		LoginPageComponent,
		LogoutPageComponent,
		RegisterPageComponent,
	],
	exports: [
		AuthenticationLayoutComponent
	],
	providers: [
		AnonymousGuard,
		AuthenticationGuard,
		AuthenticationService,
		AuthenticationStorageService,
		{ provide: AbstractSecurityStorage, useClass: AuthenticationStorageService },
		{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
	]
})
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class AuthenticationModule {
}

// https://github.com/damienbod/angular-auth-oidc-client/blob/master/projects/sample-code-flow-http-config/src/app/app.module.ts
export function configureAuth(config: AppConfiguration, storage: AuthenticationStorageService) {
	const config$ = config.appConfigLoaded$.pipe(
		take(1),
		map<unknown, OpenIdConfiguration>(_ => {
			const authenticationServerUrl = stripSlashes(config.authenticationServerUrl);
			const baseUrl = `${stripSlashes(window.location.origin)}`;
			const appUrl = `${baseUrl}${config.appRootUrl}`;
			let redirectUrl = `${appUrl}/authentication/callback`;

			// Specific return routes

			// Initiate or returns to an invitation link
			if ((location.pathname.match(/register/i) && !location.search.includes('accountEmail')) || location.pathname.includes(RoutesDefinitions.getLoginFromSignupCallbackFullPath())) {
				redirectUrl = `${appUrl}${RoutesDefinitions.getLoginFromSignupCallbackFullPath()}`;
			}

			// Initiate or returns to an invitation link
			else if (location.search.includes('accountEmail') || location.pathname.includes(RoutesDefinitions.getLoginFromInviteCallbackFullPath())) {
				redirectUrl = `${appUrl}${RoutesDefinitions.getLoginFromInviteCallbackFullPath()}`;
			}

			return {
				authority: authenticationServerUrl,
				redirectUrl,
				clientId: config.clientId,
				responseType: 'code',
				scope: 'openid profile offline_access',
				postLogoutRedirectUri: `${baseUrl}`,
				checkRedirectUrlWhenCheckingIfIsCallback: false,
				startCheckSession: false, // this seems to work only with a silentRenewUrl
				silentRenew: true,  // a timer is ticked each tokenRefreshInSeconds to check if the token is expired or not
				tokenRefreshInSeconds: 10, // value was increased because the refresh process took more than the default value and
				useRefreshToken: true, // requires offline_access scope
				ignoreNonceAfterRefresh: true, // specific to openIdDict
				renewTimeBeforeTokenExpiresInSeconds: 30,
				maxIdTokenIatOffsetAllowedInSeconds: 60, // offset in second allowed between openid server time and client time
				postLoginRoute: `${baseUrl}`,
				forbiddenRoute: `${baseUrl}`,
				unauthorizedRoute: `${baseUrl}`,
				logLevel: LogLevel.Debug, // events filtering is done in the logger service
				silentRenewTimeoutInSeconds: 30,
				historyCleanupOff: true,
				autoUserInfo: false,
				storage				
			};
		}),
	);

	return new StsConfigHttpLoader(config$);
}

